import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { setProductFilter } from "redux/productFilter";
import { updateOnlyProductsStart } from "redux/sellers";
import { getSuggestionsStart, resetSuggestions } from "redux/suggestions"; // your suggestion action
import { getSearchFilter } from "../../../utils/searchHelpers";

const DEBOUNCE_DELAY = 300;

const SearchNew = ({ params }: { params?: any }) => {
  //Shop Map
  const SHOP_MAP: { [key: string]: string } = {
    BookShop: "0592de2a-815c-4929-a521-82301b246cce",
    Comics: "3e9d0dfb-4356-466b-a664-fd0167f58b6c",
  };

  const shopOptions = Object.keys(SHOP_MAP);

  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const authors = useSelector((state: RootState) => state.authors.authors);
  const syncedFilterState = useSelector(
    (state: RootState) => state.productFilter
  );
  const currentSeller = useSelector((state: RootState) => state.seller.seller);
  const suggestions = useSelector(
    (state: RootState) => state.suggestions.suggestions
  );

  const urlParams = useParams<any>();
  const location = useLocation();
  const history = useHistory();
  const [sellerID, setSellerID] = useState(
    location.pathname === "/"
      ? SHOP_MAP[shopOptions[0]] // Default to first shop if on home page
      : urlParams?.id ||
          currentSeller?.sellerId ||
          params?.id ||
          location.pathname.split("/")[2] // Fallback to URL path
  );

  useEffect(() => {
    //set the sellerID based on the URL params or current seller
    const newSellerId =
      location.pathname === "/"
        ? SHOP_MAP[shopOptions[0]] // Default to first shop if on home page
        : urlParams?.id ||
          currentSeller?.sellerId ||
          params?.id ||
          location.pathname.split("/")[2];
    setSellerID(newSellerId);
  }, [urlParams, currentSeller, params]);

  const DEFAULT_SHOP_PATH = `/shops/${sellerID}`;
  useEffect(() => {
    console.log("Current seller ID:", sellerID);
  }, [sellerID, dispatch]);

  // Validate input: at least 2 letters/numbers/spaces/hyphens
  const isValidSearch = (text: string) => text.trim().length > 0;

  // Debounce timer ref
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // On input change: update state + debounce suggestion fetch
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    // Clear previous debounce timer if any
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // If valid input, debounce fetching suggestions
    if (isValidSearch(value.trim())) {
      debounceTimer.current = setTimeout(() => {
        dispatch(
          getSuggestionsStart({ query: value.trim(), sellerId: sellerID })
        );
      }, DEBOUNCE_DELAY);
    }
  };

  // Run search (filter dispatch + possible route change)
  const handleSearch = (searchData: string) => {
    if (!isValidSearch(searchData)) {
      console.warn("Invalid search input:", searchData);
      return; // Don't proceed with invalid input
    }
    const updatedFilter = getSearchFilter({
      searchData,
      authors,
      syncedFilterState,
    });

    dispatch(
      setProductFilter({
        sellerId: sellerID,
        page: 1,
        filter: updatedFilter,
      })
    );

    dispatch(
      updateOnlyProductsStart({
        sellerId: sellerID,
        page: 1,
        filter: updatedFilter,
        main: true,
      })
    );

    if (location.pathname !== DEFAULT_SHOP_PATH) {
      history.push(DEFAULT_SHOP_PATH);
    }
  };

  // Clear input and suggestions
  const handleClear = () => {
    setSearchValue("");
    // Optional: clear suggestions if you have an action for that
    dispatch(resetSuggestions());
  };
  useEffect(() => {
    // Reset search value and suggestions when selectedShop changes
    setSearchValue("");
    dispatch(resetSuggestions());
  }, [dispatch]);
  useEffect(() => {
    // Reset search value and suggestions when sellerID changes
    if (searchValue === "") {
      setSearchValue("");
      dispatch(resetSuggestions());
    }
  }, [searchValue]);
  // On Enter key in input, run search
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      handleSearch(searchValue);
      dispatch(resetSuggestions()); // Clear suggestions after search
    }
  };

  // When user clicks a suggestion: set input + run search
  const handleSuggestionClick = (suggestion: string) => {
    setSearchValue(suggestion);
    handleSearch(suggestion);
    dispatch(resetSuggestions());
  };

  useEffect(() => {
    // if location .pathname changes, reset search value

    //sellerID is not in the params then clear the suggestions
    if (location.pathname === "/") {
      setSearchValue("");
      dispatch(resetSuggestions()); // Clear suggestions when path changes
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/") {
      setSearchValue("");
      dispatch(resetSuggestions());
    }
  }, [location.pathname]);

  return (
    <div
      style={{
        backgroundColor: "white",
        position: "relative",
        borderRadius: "3px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <input
          style={{
            width: "100%",
            height: "50px",
            paddingLeft: "10px",
            border: "none",
            borderRadius: "3px",
            paddingRight: "40px", // space for clear button
            fontSize: "16px",
          }}
          type="text"
          value={searchValue}
          placeholder="Search by title, ISBN"
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          aria-autocomplete="list"
          aria-controls="suggestions-list"
          aria-haspopup="listbox"
          autoComplete="off"
        />
        {searchValue && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: "absolute",
              right: 100,
              background: "transparent",
              border: "none",
              fontSize: "30px",
              cursor: "pointer",
              color: "#000",
              height: "40px",
              width: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
        <button
          style={{
            marginLeft: "8px",
            height: "40px",
            position: "absolute",
            right: 10,
            width: "80px",
            borderRadius: "3px",
          }}
          className="btn-primary"
          onClick={() => {
            handleSearch(searchValue);
            dispatch(resetSuggestions()); // Clear suggestions after searchData
          }}
        >
          Search
        </button>
      </div>

      {/* Suggestions dropdown */}
      {suggestions.length > 0 && (
        <ul
          id="suggestions-list"
          role="listbox"
          style={{
            position: "absolute",
            top: "55px",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "3px",
            maxHeight: "500px",
            overflowY: "auto",
            zIndex: 1000,
            marginTop: "4px",
            paddingLeft: 0,
            listStyle: "none",
          }}
        >
          {suggestions.map((suggestion: string, index: number) => (
            <li
              key={index}
              role="option"
              tabIndex={0}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
              onClick={() => handleSuggestionClick(suggestion)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSuggestionClick(suggestion);
                }
              }}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchNew;
